import { BACKEND_REQUEST } from './api/AppSyncClient';
import { STSClient, AssumeRoleCommand } from '@aws-sdk/client-sts';
import Storage from './Util/Storage';
import { ProxyStorageKeys } from './hooks/useProxy';
import {
  createNonceAndSession,
  redirectToSSO,
  checkNewRpToken,
  customHTTPHeader,
} from './Util/AuthUtil';
import { getEnvConfigByKeyForLogin } from './config/EnvConfigForLogin';
import { getEnvConfigByKey } from './config/EnvConfig';

const securityUrl =
  getEnvConfigByKeyForLogin('securityUrl') ||
  process.env.REACT_APP_SECURITY_URL;
const giUrl =
  getEnvConfigByKeyForLogin('giUrl') || process.env.REACT_APP_GI_URL;
const clientId =
  getEnvConfigByKeyForLogin('giClientId') || process.env.REACT_APP_GI_APP;
const notificationRegion =
  getEnvConfigByKey('notificationRegion') ||
  process.env.REACT_APP_NOTIFICATION_REGION;
const notificationSTSRoleArn =
  getEnvConfigByKey('notificationSTSRoleArn') ||
  process.env.REACT_APP_NOTIFICATION_STSARN;

const isNotificationEnabled =
  getEnvConfigByKey('notificationGraphqlEndpoint') ||
  process.env.REACT_APP_NOTIFICATION_GRAPHQL_ENDPOINT;

const isImpersonating = (): boolean => {
  return (
    Storage.getItem('imprSubject') !== null &&
    Storage.getItem('imprSubject') !== '' &&
    Storage.getItem('imprUser') !== null &&
    Storage.getItem('imprUser') !== ''
  );
};

const getSTS = async (): Promise<void> => {
  const request = await fetch(`${securityUrl}/sts`, {
    method: 'post',
    headers: customHTTPHeader,
    body: JSON.stringify({
      rpToken: Storage.getItem('rpToken'),
      sessionID: Storage.getItem('sessionID'),
    }),
  });
  const response = await request.json();
  if (
    !(
      'accessKeyId' in response &&
      'expiration' in response &&
      'secretAccessKey' in response &&
      'sessionToken' in response
    )
  ) {
    throw Error('Invalid response');
  }

  const { accessKeyId, expiration, secretAccessKey, sessionToken } = response;
  Storage.setItem('accessKeyId', accessKeyId);
  Storage.setItem('expiration', expiration);
  Storage.setItem('secretAccessKey', secretAccessKey);
  Storage.setItem('sessionToken', sessionToken);

  const uri = window.location.toString();

  if (uri.indexOf('#') > 0) {
    const cleanUri = uri.substring(0, uri.indexOf('#'));
    window.history.replaceState({}, document.title, cleanUri);
  }
  try {
    // refresh notificationSTS as well (since it depends on origin request auth)
    if (isNotificationEnabled) {
      await getNotificationSTS();
    }
  } catch (exception) {
    console.error(exception);
  }
};

const getNotificationSTS = async (): Promise<void> => {
  if (!isNotificationEnabled) {
    console.error('Notifications not available');
    return;
  }
  const cred = {
    accessKeyId: Storage.getItem('accessKeyId') || '',
    secretAccessKey: Storage.getItem('secretAccessKey') || '',
    sessionToken: Storage.getItem('sessionToken') || '',
  };
  const stsClient = new STSClient({
    region: notificationRegion,
    credentials: cred,
  });

  const params = {
    RoleArn: notificationSTSRoleArn,
    RoleSessionName: 'NotificationSession',
    DurationSeconds: 3600,
  };
  const data = await stsClient.send(new AssumeRoleCommand(params));
  const credentials = data.Credentials;
  if (
    !credentials ||
    !(
      'AccessKeyId' in credentials &&
      'SecretAccessKey' in credentials &&
      'SessionToken' in credentials &&
      'Expiration' in credentials
    )
  ) {
    console.error('Invalid credentials for notification');
    return;
  }

  const { AccessKeyId, Expiration, SecretAccessKey, SessionToken } =
    credentials;
  const expirationNonNull = Expiration ?? new Date();
  Storage.setItem('notifAccessKeyId', AccessKeyId!);
  Storage.setItem(
    'notifExpiration',
    Math.floor(expirationNonNull.getTime() / 1000).toString()
  );
  Storage.setItem('notifSecretAccessKey', SecretAccessKey!);
  Storage.setItem('notifSessionToken', SessionToken!);
};

const hasValidSTS = (reqName: string): boolean => {
  const expiration =
    reqName === BACKEND_REQUEST
      ? Storage.getItem('expiration')
      : Storage.getItem('notifExpiration');

  return (
    expiration !== null &&
    parseInt(expiration, 10) > Math.floor(Date.now() / 1000)
  );
};

const isAuthenticated = async (reqName = BACKEND_REQUEST): Promise<boolean> => {
  if (hasValidSTS(reqName)) {
    // is authenticated
    return true;
  }

  // check if it is redirect from GI SSO
  if (checkNewRpToken() || Storage.getItem('rpToken')) {
    try {
      reqName === BACKEND_REQUEST ? await getSTS() : await getNotificationSTS();
      return true;
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);

      if (reqName === BACKEND_REQUEST) {
        // need to generate new session and nonce
        await createNonceAndSession(securityUrl);
        // redirect to GI SSO for rpToken
        redirectToSSO(giUrl, clientId);
      }
    }
  } else {
    // need to generate new session and nonce
    await createNonceAndSession(securityUrl);
    // redirect to GI SSO for rpToken
    redirectToSSO(giUrl, clientId);
  }
  return false;
};

const currentAccess = async (
  reqName = BACKEND_REQUEST
): Promise<{
  accessKeyId: string | null;
  secretAccessKey: string | null;
  sessionToken: string | null;
}> => {
  const isAuthenticatedResult = await isAuthenticated(reqName);

  if (isAuthenticatedResult) {
    return reqName === BACKEND_REQUEST
      ? {
          accessKeyId: Storage.getItem('accessKeyId'),
          secretAccessKey: Storage.getItem('secretAccessKey'),
          sessionToken: Storage.getItem('sessionToken'),
        }
      : {
          accessKeyId: Storage.getItem('notifAccessKeyId'),
          secretAccessKey: Storage.getItem('notifSecretAccessKey'),
          sessionToken: Storage.getItem('notifSessionToken'),
        };
  }

  return {
    accessKeyId: '',
    secretAccessKey: '',
    sessionToken: '',
  };
};

export default {
  isImpersonating,
  isAuthenticated,
  currentAccess,
};
